<template>
    <section class="o-wrapper o-wrapper--center o-page-bottom-spacing">
        <page-header>
            <slot slot="title">Checkout</slot>
            <slot slot="description">
                {{ $t('customer.checkout.instructions') }}
                <br><br>
                <p><input @change="$store.dispatch('switchBasket')" name="dropshipment" id="dropshipment" type="checkbox" v-model="basketData.IsDropshipment"/><label for="dropshipment"> {{ $t('customer.checkout.dropshipment.text') }}</label></p>
            </slot>
        </page-header>
        <section>
            <div class="c-basket-state o-page-row--large u-text-align-center">
                <router-link tag="span" class="item completed" :to="{ name: 'BasketOverview' }"><span>{{ $t('customer.checkout.basket') }}</span></router-link>
                <span class="item active"><span>{{ $t('customer.checkout.data') }}</span></span>
                <span class="item"><span>{{ $t('customer.checkout.confirmation') }}</span></span>
            </div>
            <h2 class="u-h2 u-text-align-center">{{ $t('customer.checkout.data') }}</h2>
            <div class="o-gutter-huge o-page-row--large">
                <!-- financial info -->
                <div class="c-basket__totals">
                    <div class="o-grid o-grid--half">
                        <div>
                        <div class="u-margin-bottom-small">
                            <span class="c-basket__totals__label">{{ $t('customer.checkout.total_products')}}:</span>
                            <span class="u-font-medium" v-if="articleTotal < basketData.TotalPriceNoDiscount"> <del>{{ basketData.TotalPriceNoDiscount | currency(customerCurrencySymbol) }}</del></span>
                            <span class="u-font-medium" :style="articleTotal < basketData.TotalPriceNoDiscount ? 'color:red;': ''">{{ articleTotal | currency(customerCurrencySymbol) }}</span>
                        </div>
                        <div class="u-margin-bottom-small">
                            <span class="c-basket__totals__label">
                                {{ $t('customer.checkout.delivery_cost')}}:
                            </span>
                            <template v-if="hasFreeShipping">
                                <span class="u-font-medium">{{ $t('global.free') }}</span>
                            </template>
                            <template v-else>
                                <template v-if="!basketData.IsDropshipment">
                                  <span class="u-font-medium">{{ shipmentCosts }}</span>
                                  <span class="u-font-tiny"> ({{ $t('customer.basket.remaining_for_free_delivery', { remaining: getRemaining() }) }})</span>
                                </template>
                                <template v-else>
                                    <template v-if="!hasValidDropshipmentDeliveryAddress">
                                        <span class="u-font-italic">{{ shipmentCosts }}</span>
                                    </template>
                                    <template v-else>
                                        <span class="u-font-medium">{{ shipmentCosts }}</span>
                                    </template>
                                </template>
                            </template>
                        </div>
                        <div class="u-margin-bottom-small">
                            <span class="c-basket__totals__label u-font-weight-bold">{{ $t('customer.checkout.total_ex_vat')}}:</span>
                            <span class="u-font-medium u-font-weight-bold">{{ basketTotal | currency(customerCurrencySymbol) }}</span>
                        </div>
                        </div>
                        <!-- coupon field -->
                        <div>
                        <v-form-row>
                            <v-input type="text" name="CouponCode" v-model="Coupon.Code" :fluid="false" :horizontal="true">
                                <slot slot="label">
                                    {{ $t('customer.checkout.coupon.code') }}:
                                </slot>
                                <slot slot="button">
                                    <button @click.prevent="submitCouponCode" class="c-button c-button--alpha">{{ $t('global.add') }}</button>
                                    <svgicon v-if="hasCouponApplied" name="check" width="1.3em" height="1.3em" color="green"></svgicon>
                                </slot>
                            </v-input>
                        </v-form-row>
                    </div>
                    </div>
                </div>
            </div>
            <div class="o-grid o-grid--half o-gutter-huge">
                <!-- Left half -->
                <div id="delivery-address">
                    <h3>{{ $t('customer.checkout.deliveryAddress') }}</h3>
                    <template v-if="basketData.IsDropshipment">
                        <v-form-row>
                            <v-input type="text" name="Name" v-model="basketData.DropshipmentDeliveryAddress.Name" :error="Validate.Name" :fluid="true" :horizontal="true">
                                <slot slot="label">{{ $t('customer.checkout.dropshipment.name') }}</slot>
                            </v-input>
                        </v-form-row>
                        <v-form-row>
                            <v-custom-select v-model="selectedCountry"
                            :options="deliveryCountryOptions"
                            option-name="content"
                            option-value="value"
                            track-by="value"
                            search-label="content"
                            name="delivery-address"
                            :horizontal="true"
                            :error="Validate.CountryId"
                            @change="addressChanged">
                                <slot slot="label">{{ $t('customer.account.addresses.form.country.label') }}</slot>
                            </v-custom-select>
                        </v-form-row>
                        <v-form-row>
                            <v-input type="text" name="ZipCode" v-model="basketData.DropshipmentDeliveryAddress.ZipCode" :error="Validate.ZipCode" :fluid="true" :horizontal="true" @blur="completeAddress">
                                <slot slot="label">{{ $t('customer.checkout.dropshipment.zip_code') }}</slot>
                            </v-input>
                        </v-form-row>
                        <v-form-row>
                            <v-input type="text" name="HouseNumber" v-model="basketData.DropshipmentDeliveryAddress.HouseNumber" :error="Validate.Number" :fluid="true" :horizontal="true" @blur="completeAddress">
                                <slot slot="label">{{ $t('customer.checkout.dropshipment.house_nr') }}</slot>
                            </v-input>
                        </v-form-row>
                        <v-form-row>
                            <v-input type="text" name="Street" v-model="basketData.DropshipmentDeliveryAddress.Street" :error="Validate.Street" :fluid="true" :horizontal="true">
                                <slot slot="label">{{ $t('customer.checkout.dropshipment.street') }}</slot>
                            </v-input>
                        </v-form-row>
                        <v-form-row>
                            <v-input type="text" name="City" v-model="basketData.DropshipmentDeliveryAddress.City" :error="Validate.City" :fluid="true" :horizontal="true">
                                <slot slot="label">{{ $t('customer.checkout.dropshipment.city') }}</slot>
                            </v-input>
                        </v-form-row>
                        <v-form-row>
                            <v-input type="email" name="Email" v-model="basketData.DropshipmentDeliveryAddress.Email" :error="Validate.Email" :fluid="true" :horizontal="true">
                                <slot slot="label">{{ $t('customer.checkout.dropshipment.email') }}</slot>
                            </v-input>
                        </v-form-row>
                        <v-form-row>
                            <v-input type="phone" name="Phone" v-model="basketData.DropshipmentDeliveryAddress.Phone" :error="Validate.Phone" :fluid="true" :horizontal="true">
                                <slot slot="label">{{ $t('customer.checkout.dropshipment.phone') }}</slot>
                            </v-input>
                        </v-form-row>
                    </template>
                    <template v-else>
                        <v-address-select v-model="basketData.OrderDeliveryAddress"
                                          v-if="basketData"
                                          :customer-key="parseInt(customerKey)"
                                          :add-address="true"
                                          :showContactPhone="true"
                                          :showContactEmail="true"
                                          :error="Validate.OrderDeliveryAddress">
                        </v-address-select>
                    </template>
                </div>

                <!-- right half -->
                <div>
                    <h3>{{ $t('customer.checkout.other_information') }}</h3>
                    <div class="u-margin-bottom">
                        <label for="">{{ $t('customer.checkout.own_reference')}}</label><br/>
                        <v-input type="text" name="features" size="30" :maxlength="20" v-model="basketData.Reference" :error="Validate.Reference"></v-input>
                    </div>
                    <div class="u-margin-bottom">
                        <label for="">{{ $t('customer.checkout.customer_service_notes')}}</label><br/>
                        <textarea rows="13" cols="50" name="remarksCustomerService" v-model="basketData.Remarks"></textarea>
                    </div>
                    <button class="c-button c-button--alpha" :disabled="submitDisabled" @click="checkPrepayment">{{ $t('customer.checkout.finalize_order')}}</button>
                </div>

                <!-- Prepayment modal -->
                <v-modal ref="modal">
                        <slot slot="title">{{ $t('customer.checkout.payment') }}</slot>
                        <div class="o-wrapper o-wrapper--center">
                            <form action="" class="c-form c-form--horizontal">
                                <div class="u-max-700 c-form-row">
                                    <v-form-row>
                                        <p class="u-color-base-dark">
                                            {{ $t('customer.checkout.prepayment') }}
                                        </p>
                                        <p class="u-color-base-dark">
                                            <span v-html="$t('customer.checkout.prepayment_bank_account_RA')"></span><br/>
                                        </p>
                                    </v-form-row>
                                    <v-form-row>
                                        <div class="c-form-item">
                                            <button @click.prevent="submitBasket" class="c-button c-button--alpha u-pull-right">Ok</button>
                                        </div>
                                    </v-form-row>
                                </div>
                            </form>
                        </div>
                </v-modal>
            </div>
        </section>
    </section>
</template>

<script>
    import Vue from 'vue'
    import VModal from '@/components/modal/Modal'
    import PageHeader from '@/components/common/PageHeader'
    import VInput from '@/components/form/Input'
    import VAddressSelect from '@/components/form/addressSelect/AddressSelect'
    import VCustomSelect from '@/components/form/CustomSelect'
    import VFormRow from '@/components/form/Row'
    import {mapGetters} from 'vuex'

    export default {
        components: {
            VModal,
            PageHeader,
            VInput,
            VFormRow,
            VAddressSelect,
            VCustomSelect
        },
        created (){
            this.initialDataFetch()
        },
        data() {
            return {
                Coupon : {
                    Code : "",
                    DisplayValue: "",
                    Type: "",
                    Value: ""
                },
                Validate: {},
                submitDisabled: false
            }
        },
        computed:{
            ...mapGetters({
                basketData: 'getBasket'
            }),
            customerKey() {
                return parseInt(this.$store.getters.userInfo.customerKey);
            },
            deliveryCountryOptions() {
                return this.$store.getters.AvailableCountryOptions;
            },
            selectedCountry: {
                get () {
                    let output;
                    output = this.deliveryCountryOptions.find((obj) => {
                        return obj.value === this.basketData.DropshipmentDeliveryAddress.CountryName;
                    });

                    if (output) {
                        this.$set(this.basketData.DropshipmentDeliveryAddress, 'CountryName', output.value);
                    }

                    return output ? output : {};
                },
                set (value) {
                    this.$set(this.basketData.DropshipmentDeliveryAddress, 'CountryName', value.value);
                    this.$set(this.basketData.DropshipmentDeliveryAddress, 'CountryId', value.code);
                    return value;
                }
            },
            couponPrice() {
                if (this.Coupon.Type == 0) { //fixed value
                    let basketValue = this.basketData.TotalPrice < this.basketData.TotalPriceNoDiscount ? this.basketData.TotalPrice : this.basketData.TotalPriceNoDiscount;

                    if (basketValue < this.Coupon.Value) {
                        return 0;
                    }
                    else
                    {
                        return this.basketData.TotalPriceNoDiscount - this.Coupon.Value;
                    }
                }

                if (this.Coupon.Type == 1) { //percentage

                    let totalPrice = 0;

                    this.basketData.Products.forEach((product) => {
                        product.Variants.forEach((variant) => {
                            if(variant.ProductDiscountPercentage > this.Coupon.Value)
                            {
                                totalPrice += variant.ProductTotal;
                            }
                            else
                            {
                                totalPrice += variant.ProductTotalNoDiscount * ((100 - this.Coupon.Value)/100);
                            }
                        });
                    });

                    return totalPrice;
                }

                return this.basketData.TotalPriceNoDiscount; //no coupon
            },
            hasCouponApplied() {
                return this.Coupon.Type !== "" && this.couponPrice < this.basketData.TotalPrice;
            },
            articleTotal() {
                if (this.couponPrice < this.basketData.TotalPrice) {
                    return this.couponPrice;
                }

                return this.basketData.TotalPrice;
            },
            basketTotal() {
                let result = this.articleTotal;
                if(!this.hasFreeShipping)
                {
                    if(this.basketData) {
                        if(this.basketData.IsDropshipment)
                        {
                            if(this.basketData.DropshipmentDeliveryAddress
                            && this.basketData.DropshipmentDeliveryAddress.CountryId
                            && this.basketData.DropshipmentDeliveryAddress.CountryId.length > 0
                            && this.articleTotal < this.basketData.FreeDeliveryMinimums[this.basketData.OrderDeliveryAddress.CountryId].FreeDeliveryDropShipmentCosts)
                            {
                                return result + this.basketData.DropshipmentCosts[this.basketData.DropshipmentDeliveryAddress.CountryId];
                            }
                        } else
                            {
                            if(this.basketData.OrderDeliveryAddress
                            && this.basketData.OrderDeliveryAddress.CountryId
                            && this.basketData.OrderDeliveryAddress.CountryId.length > 0
                            &&  this.basketData.FreeDeliveryMinimums[this.basketData.OrderDeliveryAddress.CountryId]
                            && this.articleTotal < this.basketData.FreeDeliveryMinimums[this.basketData.OrderDeliveryAddress.CountryId].FreeDeliveryShippingCosts)
                            {
                                return result + this.basketData.ShipmentCosts[this.basketData.OrderDeliveryAddress.CountryId];
                            }
                        }
                    }
                }

                return result;
            },
            hasValidDropshipmentDeliveryAddress() {
                return this.basketData.DropshipmentDeliveryAddress && this.basketData.DropshipmentDeliveryAddress.CountryId && this.basketData.DropshipmentDeliveryAddress.CountryId.length > 0;
            },
            shipmentCosts() {
                if(this.basketData) {
                    if(this.basketData.IsDropshipment){
                        if(this.hasValidDropshipmentDeliveryAddress)
                        {
                          if(this.articleTotal >= this.basketData.FreeDeliveryMinimums[this.basketData.OrderDeliveryAddress.CountryId].FreeDeliveryDropShipmentCosts)
                          {
                            return this.$options.filters.currency(0, this.customerCurrencySymbol);
                          } else
                          {
                            return this.$options.filters.currency(this.basketData.DropshipmentCosts[this.basketData.DropshipmentDeliveryAddress.CountryId], this.customerCurrencySymbol);
                          }
                        }
                        else
                        {
                            return this.$t('customer.basket.calculated_when_address');
                        }
                    }
                    else if(this.basketData.OrderDeliveryAddress && this.basketData.OrderDeliveryAddress.CountryId && this.basketData.OrderDeliveryAddress.CountryId.length > 0) {
                            if(this.basketData.FreeDeliveryMinimums[this.basketData.OrderDeliveryAddress.CountryId] && this.articleTotal > this.basketData.FreeDeliveryMinimums[this.basketData.OrderDeliveryAddress.CountryId].FreeDeliveryShippingCosts)
                          {
                            return this.$options.filters.currency(0, this.customerCurrencySymbol);
                          } else
                          {
                            return this.$options.filters.currency(this.basketData.ShipmentCosts[this.basketData.OrderDeliveryAddress.CountryId], this.customerCurrencySymbol);
                          }
                        }
                        else
                        {
                            return this.$t('customer.basket.calculated_when_address');
                        }
                }
            },
            hasFreeShipping() {
              let couponMax = 0;
              if (this.basketData.OrderDeliveryAddress.CountryId) {
                couponMax = this.basketData.IsDropshipment ? this.basketData.FreeDeliveryMinimums[this.basketData.OrderDeliveryAddress.CountryId].FreeDeliveryDropShipmentCosts : this.basketData.FreeDeliveryMinimums[this.basketData.OrderDeliveryAddress.CountryId].FreeDeliveryShippingCosts;
              }
                if(this.Coupon.Type == 2) return true;
                if(this.Coupon.Type == 1 && this.Coupon.Value == couponMax) return true;

                if(this.basketData.IsDropshipment) return this.articleTotal >= this.basketData.FreeDeliveryMinimums[this.basketData.OrderDeliveryAddress.CountryId].FreeDeliveryDropShipmentCosts;

                return this.basketData.OrderDeliveryAddress.CountryId && this.basketData.FreeDeliveryMinimums[this.basketData.OrderDeliveryAddress.CountryId] && this.articleTotal >= this.basketData.FreeDeliveryMinimums[this.basketData.OrderDeliveryAddress.CountryId].FreeDeliveryShippingCosts;
            },
            customerCurrencySymbol() {
                return this.$store.getters.userInfo.customerCurrencySymbol;
            }
        },
        methods:{
            initialDataFetch(){
                this.$store.dispatch("getBasket");
                this.$store.dispatch('getAvailableCountryOptions');
            },
            getRemaining(){
              if(this.basketData.OrderDeliveryAddress.CountryId && this.basketData.FreeDeliveryMinimums[this.basketData.OrderDeliveryAddress.CountryId] && this.articleTotal >= this.basketData.FreeDeliveryMinimums[this.basketData.OrderDeliveryAddress.CountryId].FreeDeliveryShippingCosts){
                return this.$options.filters.currency(0, this.customerCurrencySymbol);
              } else
              {
                return this.$options.filters.currency((this.basketData.OrderDeliveryAddress.CountryId && this.basketData.FreeDeliveryMinimums[this.basketData.OrderDeliveryAddress.CountryId] ? this.basketData.FreeDeliveryMinimums[this.basketData.OrderDeliveryAddress.CountryId].FreeDeliveryShippingCosts : 0) - this.articleTotal, this.customerCurrencySymbol);
              }
            },
            checkPrepayment(){
                this.submitDisabled = true;
                if(this.basketData.IsPrepayment)
                {
                    this.$refs.modal.openDialog();
                }
                else
                {
                    this.submitBasket();
                }
            },
            submitBasket() {
                this.$refs.modal.closeDialog();

                this.$store.dispatch("processBasket", this.basketData).then((response) => {
                    if (response.status > 300) {
                        this.validateForm(response);
                        this.submitDisabled = false;
                    } else {
                        this.$router.push({
                            name: 'BasketConfirmation',
                        })
                    }
                });
            },
            validateForm(response){
                let formErrors = response.data.ModelState;

                //Form errors => get error data from server, and transform to vue data
                Object.keys(formErrors).forEach(key => {
                    if(key.includes('model')){
                        key = key.replace('model.', '');
                        formErrors[key] = formErrors['model.'+key][0];
                        delete formErrors['model.'+key];
                    }
                    else{
                        this.$notify({
                            group: 'global',
                            title: formErrors[key][0],
                            type: 'error',
                            duration: 4000
                        });
                    }
                });

                this.Validate = formErrors;
            },
            submitCouponCode() {
                // Prevent empty codes from going to an invalid URL (and logging the user out)
                if (this.Coupon.Code == null || this.Coupon.Code.trim() === '') {
                    this.Coupon.Value = "";
                    this.Coupon.DisplayValue = "";
                    this.Coupon.Type = "";

                    this.basketData.CouponCode = "";

                    return;
                }

                this.$store.dispatch("submitCouponCode", { Code: this.Coupon.Code}).then((result) => {
                    if(result.Error) {
                        this.$notify({
                            group: 'global',
                            title: result.Error,
                            type: 'error',
                            duration: 4000
                        });
                        this.Coupon.Value = "";
                        this.Coupon.DisplayValue = "";
                        this.Coupon.Type = "";

                        this.basketData.CouponCode = "";
                    } else {

                        this.Coupon.Value = result.Value;
                        this.Coupon.DisplayValue = result.DisplayValue;
                        this.Coupon.Type = result.Type;

                        this.basketData.CouponCode = this.Coupon.Code;

                        if(this.couponPrice > this.basketData.TotalPrice)
                        {
                            this.Coupon.Value = "";
                            this.Coupon.DisplayValue = "";
                            this.Coupon.Type = "";

                            this.basketData.CouponCode = "";

                            this.$notify({
                                group: 'global',
                                title: this.$t('customer.checkout.higher_discount'),
                                type: 'info',
                                duration: 4000
                            });
                        }
                    }
                }).catch((response) => {
                    this.$notify({
                        group: 'global',
                        title: response.statusText,
                        type: 'error',
                        duration: 4000
                    });
                });
            },
            addressChanged() {
                this.completeAddress();
            },
            completeAddress() {
                if((this.selectedCountry && this.selectedCountry.code == "NL")
                && (this.basketData.DropshipmentDeliveryAddress.ZipCode && this.basketData.DropshipmentDeliveryAddress.ZipCode.length > 0)
                && (this.basketData.DropshipmentDeliveryAddress.HouseNumber && this.basketData.DropshipmentDeliveryAddress.HouseNumber.length > 0))
                {
                    //Do call to maybe autocomplete street
                    this.$store.dispatch('getStreet', { postalCode: this.basketData.DropshipmentDeliveryAddress.ZipCode, houseNr: this.basketData.DropshipmentDeliveryAddress.HouseNumber }).then((response) => {
                        this.basketData.DropshipmentDeliveryAddress.Street = response.street;
                    });
                }
            },
        }
    }
</script>

<style lang="scss" scoped>

    @import "~@/assets/scss/private";

    .c-selected-address {
        border: 1px solid palette(color-neutral, medium);
        padding: em($global-spacing-unit-tiny);
        margin-top: em($global-spacing-unit-small);
        margin-bottom: em($global-spacing-unit-small);
    }

    .c-basket__totals {
        border: 1px solid $color-neutral;
        padding: 30px 30px;
        margin-left: 6em;
        &__label {
            display: inline-block;
            min-width: 170px;
        }
    }
</style>

<style lang="scss">
    #delivery-address label {
        min-width:100px;
    }
</style>



// WEBPACK FOOTER //
// src/views/customer/basket/Checkout.vue